<script setup lang="ts">
import type { UserProfile } from "./utils";
import { createReusableTemplate } from "@vueuse/core";
import { UserRoundIcon } from "lucide-vue-next";
import { AvatarFallback, AvatarImage, AvatarRoot } from "reka-ui";
import { computed } from "vue";
import { removeEmojis } from "@/common/lib/string";
import { useDisplayName } from "../user/useDisplayName";
import { getAvatarBackgroundColor, getUserInitials, getUserName } from "./utils";

const props = defineProps<{
  user: UserProfile;
}>();

const displayName = useDisplayName(() => props.user);
const iconBackgroundColor = computed(() => {
  if (props.user.isAnonymous && props.user.color) {
    return props.user.color;
  }
  return getAvatarBackgroundColor(displayName.value);
});
const key = computed(() => `${props.user.userId}-${props.user.profileImageUrl}`);
const initials = computed(() => {
  // guard against the case when display name starts with emoji
  const name = removeEmojis(displayName.value).trim() || getUserName(props.user.email);
  return getUserInitials(name);
});

const [DefineTemplate, ReuseTemplate] = createReusableTemplate();
</script>

<template>
  <DefineTemplate>
    <div
      class="flex size-full items-center justify-center overflow-hidden rounded-full leading-none font-semibold text-white uppercase select-none"
      aria-hidden="true"
      :style="{ backgroundColor: iconBackgroundColor }"
    >
      <template v-if="user.isAnonymous">
        <UserRoundIcon class="size-4 flex-none" aria-hidden="true" />
      </template>
      <template v-else>
        {{ initials }}
      </template>
    </div>
  </DefineTemplate>
  <AvatarRoot
    :key="key"
    v-bind="$attrs"
    class="inline-block rounded-full"
  >
    <AvatarImage
      v-if="user.profileImageUrl"
      class="aspect-square rounded-full border border-slate-200 object-cover"
      :src="user.profileImageUrl"
      :alt="displayName"
    />
    <ReuseTemplate v-else />
    <AvatarFallback v-if="user.profileImageUrl" :style="{ backgroundColor: iconBackgroundColor }" :delay-ms="250">
      <ReuseTemplate />
    </AvatarFallback>
  </AvatarRoot>
</template>
